// retry-policy — when the "Retry" button is offered for a job.
//
// Q-02: Retry was shown for failures that can never succeed on a second
// run (unsupported source, unsupported media, source gone, bad input).
// The API (R-14) will say so explicitly with `retryable: false`; until
// every row carries the field we fall back to the category.
//
// Pure module — unit-tested in node.

import type { TranslationErrorCategory, TranslationJob } from '@nayto/types';

/** Categories that are final by definition: retrying re-runs the same
 * extractor on the same input and fails the same way. */
export const NON_RETRYABLE_CATEGORIES: ReadonlySet<TranslationErrorCategory> = new Set([
  'unsupported_source',
  'unsupported_media',
  'source_unavailable',
  'user_fixable',
]);

export type RetryJobShape = Pick<TranslationJob, 'status' | 'retryCount'> & {
  retryable?: boolean | null;
  errorCategory?: TranslationErrorCategory | string | null;
};

export function canRetryJob(job: RetryJobShape, limit: number): boolean {
  if (job.status !== 'failed' && job.status !== 'complete') return false;
  if (job.retryCount >= limit) return false;
  // API ≥ R-14: the server's verdict wins in both directions.
  if (typeof job.retryable === 'boolean') return job.retryable;
  // Older API: hide for the categories that are final by definition.
  if (job.status === 'failed' && job.errorCategory) {
    return !NON_RETRYABLE_CATEGORIES.has(job.errorCategory as TranslationErrorCategory);
  }
  return true;
}

/** True when a second run can never succeed: the server said so
 * (`retryable: false`) or the category is final by definition. Drives
 * both the Retry button and the failure title (no "retry" wording). */
export function isFinalFailure(job: Pick<RetryJobShape, 'retryable' | 'errorCategory'>): boolean {
  if (job.retryable === false) return true;
  if (job.retryable === true) return false;
  return (
    job.errorCategory != null &&
    NON_RETRYABLE_CATEGORIES.has(job.errorCategory as TranslationErrorCategory)
  );
}
